import React, { useState, useEffect } from 'react';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import api from '../../services/api';
import Modal from '../Modal';
import { TrashIcon, PencilIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const EmailGateways = () => {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: '',
    secure: true,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    isDefault: false
  });

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/email-gateways');
      if (response.data.success) {
        setGateways(response.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch email gateways');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      host: '',
      port: '',
      secure: true,
      username: '',
      password: '',
      fromEmail: '',
      fromName: '',
      isDefault: false
    });
    setEditingGateway(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingGateway) {
        const response = await api.put(`/admin/email-gateways/${editingGateway._id}`, formData);
        if (response.data.success) {
          toast.success('Email gateway updated successfully');
        }
      } else {
        const response = await api.post('/admin/email-gateways', formData);
        if (response.data.success) {
          toast.success('Email gateway created successfully');
        }
      }
      setShowModal(false);
      resetForm();
      fetchGateways();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save email gateway');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (gateway) => {
    setEditingGateway(gateway);
    setFormData({
      name: gateway.name,
      host: gateway.host,
      port: gateway.port,
      secure: gateway.secure,
      username: gateway.username,
      fromEmail: gateway.fromEmail,
      fromName: gateway.fromName,
      isDefault: gateway.isDefault,
      password: '' // Don't populate password for security
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email gateway?')) {
      return;
    }
    try {
      const response = await api.delete(`/admin/email-gateways/${id}`);
      if (response.data.success) {
        toast.success('Email gateway deleted successfully');
        fetchGateways();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete email gateway');
    }
  };

  const handleTestEmail = async (id) => {
    try {
      setTestLoading(true);
      const response = await api.post(`/admin/email-gateways/${id}/test`, {
        testEmail: formData.fromEmail // Using from email as test recipient
      });
      if (response.data.success) {
        toast.success('Test email sent successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestLoading(false);
    }
  };

  if (loading && !gateways.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Email Gateways</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Add Gateway
        </button>
      </div>

      {/* Gateways List */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {gateways.map((gateway) => (
              <tr key={gateway._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-900">{gateway.name}</div>
                    {gateway.isDefault && (
                      <CheckCircleIcon className="ml-2 h-5 w-5 text-green-500" title="Default Gateway" />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {gateway.host}:{gateway.port}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {gateway.fromEmail}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${gateway.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {gateway.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {/* <button
                    onClick={() => handleTestEmail(gateway._id)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                    disabled={testLoading}
                  >
                    Test
                  </button> */}
                  <button
                    onClick={() => handleEdit(gateway)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(gateway._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingGateway ? 'Edit Email Gateway' : 'Add Email Gateway'}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Gateway Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Gateway Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="Primary SMTP"
                required
              />
            </div>

            {/* SMTP Server */}
            <div>
              <label className="block text-sm font-medium text-gray-700">SMTP Server</label>
              <input
                type="text"
                name="host"
                value={formData.host}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="smtp.example.com"
                required
              />
            </div>

            {/* Port */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Port</label>
              <input
                type="number"
                name="port"
                value={formData.port}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="587"
                required
              />
            </div>

            {/* Secure */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="secure"
                checked={formData.secure}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Use SSL/TLS
              </label>
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="username@example.com"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password {editingGateway && '(leave blank to keep current)'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="••••••••"
                required={!editingGateway}
              />
            </div>

            {/* From Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">From Email</label>
              <input
                type="email"
                name="fromEmail"
                value={formData.fromEmail}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="noreply@example.com"
                required
              />
            </div>

            {/* From Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">From Name</label>
              <input
                type="text"
                name="fromName"
                value={formData.fromName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="Company Name"
                required
              />
            </div>

            {/* Default Gateway */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Set as Default Gateway
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {loading ? 'Saving...' : 'Save Gateway'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EmailGateways; 